import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Col, Container, Form, Label, Modal, ModalBody, Row } from 'reactstrap';
import useApi from 'vl-common/src/hooks/useApi';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import { LoadingButton } from 'vl-common/src/components/LoadingButton';

interface RemoveQuotaProps {
  open: boolean;
  caseId?: number;
  clientCaseId?: string;
  onCancel: () => void;
  onCreated: () => void;
}

export const RemoveQuotaModal = ({ open, caseId, clientCaseId, onCancel, onCreated }: RemoveQuotaProps) => {
  const { t } = useTranslation('common');
  const { Api, consumed } = useApi();
  const queryClient = useQueryClient();
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState({
    visibility: false,
    msg: ''
  });
  const { data, isLoading } = useQuery({
    queryKey: ['removeQuotaCheck', caseId],
    queryFn: () => Api.cases.removeAuth(caseId, 1).then((res) => res.data),
    enabled: !!caseId && open
  });
  const displayAlertMessage = (msg = '') => {
    setShowAlertMessage({ visibility: true, msg });
  };
  const hideAlertMessage = () => {
    setShowAlertMessage({ visibility: false, msg: '' });
  };
  const removeQuota = async () => {
    setSaveInProgress(true);
    try {
      await Api.cases.removeAuth(caseId, 0);
      setSaveInProgress(false);
      onCreated();
    } catch (e) {
      displayAlertMessage('Unable to remove quota. Please try again');
      setSaveInProgress(false);
      consumed(e);
    } finally {
      await queryClient.invalidateQueries({ queryKey: ['get-taskslist'] });
    }
  };

  return (
    <Modal size="lg" isOpen={open} centered className="vl-add-modal" data-testid="remove-quota-modal">
      <ModalBody className="p-4">
        <Container>
          <Row>
            <Col xs="12">
              <span className="vl-modal-title" data-testid="remove-quota-header">
                {t('Remove Quota')}
              </span>
            </Col>
            <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
            <Col>
              {isLoading && <div data-testid="loading-state">{t('Loading Quota details...')}</div>}
              {!isLoading && (
                <Form className="mt-4">
                  <Col style={styles.info}>{t('This will remove quota for the following:')}</Col>
                  <Col>
                    <Label style={styles.label}>{t('Client Case Ref:')}</Label>
                    <Label style={styles.label}>
                      <span className="vl-primary-dark-text">{clientCaseId}</span>
                    </Label>
                  </Col>
                  <Col>
                    <Label style={styles.label}>{t('Appointment Type:')}</Label>
                    <Label style={styles.label}>
                      <span className="vl-primary-dark-text">{data?.appt_type_desc}</span>
                    </Label>
                  </Col>
                  <Col>
                    <Label style={styles.label}>{t('Clinician Type:')}</Label>
                    <Label style={styles.label}>
                      <span className="vl-primary-dark-text">{data?.clin_subtype_desc}</span>
                    </Label>
                  </Col>
                  <Col style={styles.info}> {t('Are you sure you want to proceed?')}</Col>
                </Form>
              )}
            </Col>
            <Col xs="12" className="text-right" style={styles.buttonContainer}>
              <Button
                className="vl-button-gray vl-font-regular"
                type="button"
                disabled={saveInProgress}
                onClick={() => {
                  hideAlertMessage();
                  onCancel?.();
                }}
                data-testid="cancel-remove-quota-modal-button"
              >
                {t('Cancel')}
              </Button>
              <LoadingButton
                className="vl-button vl-font-regular"
                onClick={() => {
                  hideAlertMessage();
                  removeQuota().then();
                }}
                loading={saveInProgress}
                disabled={saveInProgress}
                data-testid="remove-quota-button"
              >
                {t('Remove Quota')}
              </LoadingButton>
            </Col>
          </Row>
        </Container>
      </ModalBody>
    </Modal>
  );
};
const styles = {
  info: { marginBottom: '1rem' },
  label: { marginBottom: '1rem', width: '50%' },
  buttonContainer: { display: 'flex', justifyContent: 'flex-end' }
};

export default RemoveQuotaModal;
